import {
  type LayoutSkeleton,
  layoutSkeletonSchema,
} from '../composables/data-fetching/auto-layout/index.types'
import type { UserFilteringData } from '../plugins/1.filters'
import { useConfigStore } from './feature-flags/useConfigStore'

/**
 * If device is mobile, modifies the layout data to remove all ads from the first section and only returns one per remaining sections.
 * @param layoutData - The layout data to be modified
 * @returns {Promise<LayoutSkeleton>} - The modified layout data
 */
export const getDeviceLayout = (
  layoutData: LayoutSkeleton | undefined
): LayoutSkeleton => {
  if (!layoutData) {
    throw new Error('Layout data is undefined or null')
  }

  const layoutCopy = structuredClone(layoutData)
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const { $filters } = useNuxtApp() as unknown as {
    $filters: UserFilteringData
  }
  if (!$filters) {
    throw new Error(
      'Invalid or missing $filters in Nuxt app. Expected $filters to be an object with an "isMobile" property.'
    )
  }
  // no layout change needed
  if (!$filters?.isMobile || !layoutCopy.section) return layoutCopy

  // Note: Currently, there are no defined rules for handling ads in mobile layouts.
  // This is a temporary solution, explicit rules should be defined to handle this logic more robustly.
  layoutCopy?.section.map((section, index) => {
    if (index === 0 || !section.ads?.length) {
      section.ads = []
      return
    }

    // target last index of x to avoid 2 ad units in a row
    const selectedAd = section.ads?.filter((ad) => ad.x === -1).slice(-1)
    section.ads = selectedAd ? selectedAd : section.ads.slice(-1)
  })
  return layoutCopy
}

/**
 * Pinia store for managing the layout skeleton
 * This store fetches the layout skeleton from a remote CDN and provides methods to refresh it
 * It also handles loading state and provides a reactive reference to the layout skeleton
 */
export const useLayoutSkeletonStore = useConfigStore<
  LayoutSkeleton | undefined
>({
  configStoreKey: 'layoutSkeleton',
  defaults: undefined,
  schema: (data) => layoutSkeletonSchema.parse(data),
  transform: getDeviceLayout,
})
